import { useUserStore } from '~/stores/user';
import type { Rollout } from '@/types';
import type { UserRoleValues } from '@/config/user-roles';
import type { RolloutPlanValues } from '@/config/rollout-plans';
import { useRouter } from '#imports';
import type { RouteRecordRaw } from 'vue-router';
import { joinURL } from 'ufo';
import { klona } from 'klona';
import { isNavItemAccessible } from '@/util/navigation';

// @ts-expect-error virtual file
import routes from '#build/routes';

type NavigationItemPrototype = {
  name: string;
  href: string;
  order: number;
  global?: boolean;
  hideInNavigation?: boolean;
  divideBefore?: boolean;
  renderExact?: boolean;
  permittedRoles?: UserRoleValues[];
  permittedPlan?: RolloutPlanValues;
  subNavigation?: SubNavigationItem[];
};

export type NavigationItem = Omit<NavigationItemPrototype, 'divideBefore'>;

export type SubNavigationItem = Omit<NavigationItemPrototype, 'subNavigation'>;

function normalizeRoutes(
  routes: RouteRecordRaw[],
  prefix = '/'
): NavigationItem[] {
  const _routes: NavigationItem[] = [];
  for (let route of routes) {
    const { children, path } = route;
    const defaultChild = children?.findIndex((p) => !p.path || p.path === '/');
    if (typeof defaultChild !== 'undefined' && defaultChild >= 0) {
      route = children![defaultChild];
    }
    _routes.push({
      href: joinURL(prefix, path),
      renderExact: !path,
      ...route.meta,
      name: (route.name as string) ?? 'error-not-defined',
      order: route.meta?.order as number,
      subNavigation: children
        ? normalizeRoutes(children, joinURL(prefix, path))
        : undefined,
    });
  }
  return _routes
    .filter((r) => !r.hideInNavigation)
    .sort((a, b) => a.order - b.order);
}

const items = normalizeRoutes(klona(routes));

export function useRolloutRouting() {
  const userStore = useUserStore();
  const router = useRouter();
  const selectedRollout = computed<Rollout | false>({
    get() {
      return (
        userStore.availableRollouts.find(
          (rollout) =>
            rollout.identifier ===
            router.currentRoute.value.params.rolloutIdentifier
        ) ?? false
      );
    },
    set(rollout) {
      if (!rollout) {
        return router.push({
          name: 'home',
        });
      }
      router.push({
        name: 'rollout-project-rolloutIdentifier-rollout',
        params: { rolloutIdentifier: rollout.identifier },
      });
    },
  });

  const getRolloutRelativeLink = (href: string) => {
    const safeHref = href.startsWith('/') ? href : `/${href}`;
    return selectedRollout.value
      ? '/rollout-project/' + selectedRollout.value.identifier + safeHref
      : safeHref;
  };

  const fixSubNavigationItem = (subItem: SubNavigationItem) => {
    // @ts-expect-error this is the case for /rollout-project/:rolloutIdentifier()/inventory/clients
    if (subItem.subNavigation) {
      const newItem: NavigationItem = subItem;
      if (newItem.subNavigation && newItem.subNavigation.length) {
        const actualChild = newItem.subNavigation[0];
        subItem = {
          ...actualChild,
          renderExact: false,
        };
      }
    }
    return subItem;
  };

  const visibleItems = computed(() => {
    return items
      .filter((item) => {
        return isNavItemAccessible(
          item,
          selectedRollout.value,
          userStore.user?.role
        );
      })
      .map((item) => {
        return {
          ...item,
          subNavigation: item.subNavigation
            ?.map((subItem) => fixSubNavigationItem(subItem))
            .sort((a, b) => a.order - b.order),
        };
      });
  });

  const subNavigation = computed(() => {
    if (
      currentItems.value.mainNavigationItem &&
      currentItems.value.mainNavigationItem.subNavigation
    ) {
      let divideBefore = false;
      const items: SubNavigationItem[] =
        currentItems.value.mainNavigationItem.subNavigation
          .map((item) => fixSubNavigationItem(item))
          .sort((a, b) => a.order - b.order)
          .reduce((result, item) => {
            if (
              isNavItemAccessible(
                item,
                selectedRollout.value,
                userStore.user?.role
              )
            ) {
              if (divideBefore) {
                divideBefore = false;
                result.push({
                  ...item,
                  divideBefore: true,
                });
              } else {
                result.push(item);
              }

              return result;
            } else if (item.divideBefore) {
              // Pass `divideBefore` to the next item in case the current one is not shown
              divideBefore = true;
            }
            return result;
          }, [] as SubNavigationItem[]);

      return items.length ? items : false;
    }
    return false;
  });

  const currentItems = computed(() => {
    const currentRoute = router.currentRoute.value;
    const matchedRoutes = currentRoute.matched.map((i) => i.path);
    const mainNavigationItem = items
      .filter((item) => matchedRoutes.includes(item.href))
      .sort((a, b) => a.order - b.order)
      .pop();
    // TODO: the current subNavigationItem is incorrect
    const subNavigationItem = mainNavigationItem?.subNavigation?.find(
      (item) => typeof item === 'object' /* && item.current */
    );
    return {
      mainNavigationItem,
      subNavigationItem,
    };
  });

  return {
    items,
    selectedRollout,
    visibleItems,
    subNavigation,
    currentItems,
    getRolloutRelativeLink,
  };
}
